import { useEffect, useRef, useState } from 'react';
import { BORNES_DES_HEURES, BORNES_DES_MINUTES, casesDeLaDuree, issueDesCases } from './casesEnMinutes';
import { entreeRange, quitteLaLigne } from './saisieTapee';
import { HEURES_DE_LA_DUREE, MINUTES_DE_LA_DUREE, UNITE_DES_HEURES, UNITE_DES_MINUTES } from '../partage/vocabulaire';
import './CasesDeLaDuree.css';

/**
 * Ce qu'une tâche te prend, en deux petites cases côte à côte, heures puis minutes (chantier
 * duree-a-la-minute, ticket 03) : on tape 1 et 25, la tâche prend 1 h 25.
 *
 * Les règles des champs tapés de « Quand » : rien ne s'écrit pendant la frappe ; le total
 * s'écrit, entier, quand on quitte les deux cases ou sur Entrée, qui range aussi les cases
 * (`onFini`) ; passer de l'une à l'autre
 * n'écrit rien (le total dépend des deux). Échap rend la valeur d'avant, sans fermer la fiche.
 * La règle (une case vide compte 0, 0 et 0 enlève, au-delà de 24 h refusé) est dans
 * `casesEnMinutes.ts`.
 */
export function CasesDeLaDuree({
  effortMin,
  onEcrire,
  onFini,
}: {
  effortMin: number | null;
  onEcrire: (effortMin: number | null) => void;
  /**
   * Entrée range les deux cases (retour d'Igor du 2026-10-11, ticket 03 rouvert) : la durée
   * s'écrit, ou la valeur d'avant revient si elle est impossible, puis la fiche replie le réglage.
   */
  onFini?: () => void;
}) {
  const [cases, setCases] = useState(() => casesDeLaDuree(effortMin));
  const caseHeures = useRef<HTMLInputElement>(null);
  const caseMinutes = useRef<HTMLInputElement>(null);
  /* Le dernier total envoyé : Entrée puis la sortie ne l'envoient pas deux fois avant que `effortMin` suive. */
  const envoye = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    setCases(casesDeLaDuree(effortMin));
    envoye.current = undefined;
  }, [effortMin]);

  /* Les cases reprennent la valeur d'avant, jusque dans ce que le navigateur tenait à moitié tapé. */
  function rendre() {
    const avant = casesDeLaDuree(effortMin);
    setCases(avant);
    if (caseHeures.current) caseHeures.current.value = avant.heures;
    if (caseMinutes.current) caseMinutes.current.value = avant.minutes;
  }

  function finir() {
    const h = caseHeures.current;
    const m = caseMinutes.current;
    if (!h || !m) return;
    const issue = issueDesCases(h.value, m.value, effortMin, h.validity.badInput || m.validity.badInput);
    if (issue.sorte === 'revenir') rendre();
    else if (issue.sorte === 'ecrire' && issue.effortMin !== envoye.current) {
      envoye.current = issue.effortMin;
      onEcrire(issue.effortMin);
    }
  }

  return (
    <span
      className="cases-duree"
      onKeyDown={(e) => {
        if (entreeRange(e.key, (e.target as HTMLElement).tagName, e.nativeEvent.isComposing)) {
          // Comme une ligne de « Quand » : Entrée enregistre et range.
          e.preventDefault();
          finir();
          onFini?.();
        } else if (e.key === 'Escape') {
          // Échap n'annule que la saisie : sans ceci, il remonte jusqu'à la fenêtre, où la fiche
          // le prend pour elle et se ferme.
          e.stopPropagation();
          rendre();
        }
      }}
      onBlur={(e) => {
        if (quitteLaLigne(e.currentTarget, e.relatedTarget)) finir();
      }}
    >
      <label className="cases-duree-une">
        <input
          ref={caseHeures}
          type="number"
          inputMode="numeric"
          className="champs-nombre cases-duree-case"
          data-tuto="duree-heures"
          aria-label={HEURES_DE_LA_DUREE}
          min={BORNES_DES_HEURES.min}
          max={BORNES_DES_HEURES.max}
          step={BORNES_DES_HEURES.pas}
          placeholder="0"
          autoFocus
          value={cases.heures}
          onChange={(e) => setCases((c) => ({ ...c, heures: e.target.value }))}
        />
        {UNITE_DES_HEURES}
      </label>
      <label className="cases-duree-une">
        <input
          ref={caseMinutes}
          type="number"
          inputMode="numeric"
          className="champs-nombre cases-duree-case"
          data-tuto="duree-minutes"
          aria-label={MINUTES_DE_LA_DUREE}
          min={BORNES_DES_MINUTES.min}
          max={BORNES_DES_MINUTES.max}
          step={BORNES_DES_MINUTES.pas}
          placeholder="0"
          value={cases.minutes}
          onChange={(e) => setCases((c) => ({ ...c, minutes: e.target.value }))}
        />
        {UNITE_DES_MINUTES}
      </label>
    </span>
  );
}
